import {
  authSession,
  refreshToken,
} from "@/authentication/authenticationService"
import { ExternalRoute } from "@/constants/externalRoute.ts"
import nativeHelper from "@/lib/nativeHelper.ts"
import { removeLeadingSlash } from "@/lib/utils.ts"
import { getPublicWebsiteUrl } from "@/lib/websiteUrls.ts"
import shouldServeNexus from "@/middleware/shouldServeNexus.ts"
import { EdgeToEdge } from "@capawesome/capacitor-android-edge-to-edge-support"
import {
  InAppBrowser,
  OpenWebViewOptions,
  ToolBarType,
  UrlEvent,
} from "@capgo/inappbrowser"
import { useThrottleFn } from "@vueuse/core"

const WEBVIEW_THROTTLE_OPEN_IN_MS = 1000

const onLinkClicked = async (
  event: MouseEvent,
  targetUrl: string,
  isLegacyAppLink: boolean,
) => {
  if (nativeHelper.isNative() && isLegacyAppLink) {
    event.preventDefault()
    const fullyQualifiedUrl = targetUrl.startsWith("http")
      ? targetUrl
      : `${getPublicWebsiteUrl()}${removeLeadingSlash(targetUrl)}`

    await openBrowser(fullyQualifiedUrl)
    await InAppBrowser.addListener("urlChangeEvent", onUrlChanged)
  }
}
const openBrowser = useThrottleFn(async (url: ExternalRoute) => {
  const options: OpenWebViewOptions = {
    activeNativeNavigationForWebview: true,
    disableGoBackOnNativeApplication: false,
    showArrow: false,
    showReloadButton: false,
    visibleTitle: true,
    toolbarType: ToolBarType.ACTIVITY,
    toolbarColor: "#faf7f4",
    title: "",
    url,
  }
  type MessageFromWebviewEvent = {
    detail: Record<string, unknown>
  }
  const fetchAccessToken = async (event: MessageFromWebviewEvent) => {
    if (event?.detail?.message !== "requestToken") return

    await refreshToken()
    const { accessToken, accessTokenExpiry } = authSession
    const tokenData = {
      token: accessToken.value,
      expiryDate: accessTokenExpiry.value,
    }
    InAppBrowser.postMessage({
      detail: { message: { type: "accessTokenUpdated", data: tokenData } },
    })
  }

  const sendLoadedEventToInAppBrowser = () =>
    InAppBrowser.postMessage({
      detail: { message: { type: "webviewLoaded" } },
    })

  const removeAllListeners = async () => {
    await InAppBrowser.removeAllListeners()
  }

  InAppBrowser.addListener("browserPageLoaded", sendLoadedEventToInAppBrowser)
  InAppBrowser.addListener("messageFromWebview", fetchAccessToken)

  if (nativeHelper.getPlatform() === "android") {
    await EdgeToEdge.disable()
  }

  await InAppBrowser.openWebView(options)

  await InAppBrowser.addListener("closeEvent", async () => {
    await removeAllListeners()

    if (nativeHelper.getPlatform() === "android") {
      await EdgeToEdge.enable()
    }
  })
}, WEBVIEW_THROTTLE_OPEN_IN_MS)

/**
 * once the in-app browser navigates to a page on the same parent domain
 * as the app but which is not an external route, we can assume it's done
 * whatever it needed to do, close it, but then we'll potentially be in
 * the wrong location so let's navigate to the url the browser attempted
 * to hit. In order to make sure we gert fresh state we'll force a reload
 */
const onUrlChanged = async (event: UrlEvent) => {
  const url = new URL(event.url)
  const publicWebsiteUrl = new URL(getPublicWebsiteUrl())

  if (
    url.hostname === publicWebsiteUrl.hostname &&
    shouldServeNexus(url.pathname, url.hostname, url.hostname, import.meta.env)
  ) {
    await InAppBrowser.removeAllListeners()
    await InAppBrowser.close()

    window.location.assign(`${url.pathname}${url.search}`)
  }
}

export default {
  onLinkClicked,
  openBrowser,
  onUrlChanged,
}
